import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Typography from '~components/typography';
import { useBookingProcessContenxt } from '~containers/booking-process/booking-process-context';
import { useTranslation } from '~i18n';
import { IconMore } from '~svg-components';
import currencyformatter from '~utils/currency-format';
import { mousewheel } from '~utils/js-helper';
import styles from './index.module.scss';

interface IAmendmentProps {
  title: string;
  totalPrice: number;
  totalPriceOriginal: number;
  isPayToPartner?: boolean;
}
interface PaymentValueProps {
  amountDue?: number | null | undefined;
  totalPrice?: number | null | undefined;
  totalPriceOriginal?: number | null | undefined;
  installmentName?: string | null | undefined;
  isPayToPartner?: boolean | null | undefined;
}

interface IBlackBarProps {
  status:
    | 'base'
    | 'paymentPlan'
    | 'personalDetails'
    | 'tenancy'
    | 'payment'
    | 'selectBed'
    | 'checkin'
    | 'stay'
    | 'myPayment'
    | 'amendment';
  onHandleClick?: () => void;
  roomType?: any;
  tenancyDate?: any;
  paymentValue?: PaymentValueProps | null | undefined;
  bannerText?: any;
  task?: string;
  taskCompleted?: string;
  paymentTitle?: string;
  paymentLabel?: string;
  amendmentValue?: IAmendmentProps;
  isExtension?: boolean;
  baseTitle?: React.ReactNode;
  baseValue?: React.ReactNode;
  specialRateOffered?: Boolean | null | undefined;
}

const BlackBar: React.FC<IBlackBarProps> = props => {
  const { t } = useTranslation('student_portal');

  const {
    status,
    onHandleClick,
    roomType,
    tenancyDate,
    paymentValue,
    bannerText,
    task,
    taskCompleted,
    paymentTitle,
    paymentLabel,
    amendmentValue,
    isExtension,
    baseTitle,
    baseValue,
  } = props;
  const { property, isRollingBooking } = useBookingProcessContenxt();
  const [isHideBar, setIsHideBar] = useState(false);

  useEffect(() => {
    mousewheel(
      window,
      () => setIsHideBar(true),
      () => setIsHideBar(false)
    );
  });

  const roomLabel = roomType?.category && t(`select_bed.room.${roomType.category.toLowerCase()}`);

  return (
    <div
      className={classNames(styles.root, { [styles.blackBarHidden]: isHideBar })}
      onClick={() => {
        if (onHandleClick) onHandleClick();
      }}
    >
      {onHandleClick && (
        <div className={styles.more}>
          <IconMore fill="var(--color-white)" />
        </div>
      )}
      {status === 'selectBed' && roomType && (
        <>
          <div>
            <Typography variant="body3" color="var(--color-white)">
              {roomType.name}
            </Typography>
            <Typography className="black-bar__label" variant="body3" color="var(--color-white)">
              {roomLabel}
            </Typography>
          </div>
          <div>
            <Typography variant="body3" color="var(--color-purple_4)">
              {t('select_bed.tenancy_option.move_in')}
            </Typography>
            <Typography className={styles.date} variant="body3" color="var(--color-white)">
              {moment(tenancyDate.startDate).format('ddd, ll')}
            </Typography>

            {!isRollingBooking && (
              <>
                <Typography variant="body3" color="var(--color-purple_4)">
                  {t('select_bed.tenancy_option.move_out')}
                </Typography>
                <Typography className={styles.date} variant="body3" color="var(--color-white)">
                  {moment(tenancyDate.endDate).format('ddd, ll')}
                </Typography>
              </>
            )}
          </div>
        </>
      )}
      {(status === 'paymentPlan' || status === 'personalDetails' || status === 'tenancy') &&
        paymentValue && (
          <>
            <Typography component="p" variant="body3" color="var(--color-white)" fontWeight={600}>
              {t('checkout_card.payment_at_booking.title')}: {property?.currency}{' '}
              {currencyformatter(paymentValue?.amountDue || 0, property?.currency)}
            </Typography>
            {!paymentValue?.isPayToPartner && (
              <div className={styles.blackBarItem}>
                <Typography variant="body3" color="var(--color-white)">
                  {t('checkout_card.price_summary.total_price')}: {property?.currency}{' '}
                  {currencyformatter(paymentValue?.totalPrice || 0, property?.currency)}{' '}
                </Typography>
                {paymentValue?.totalPriceOriginal !== paymentValue?.totalPrice && (
                  <Typography
                    variant="body3"
                    color="var(--color-grey_99)"
                    className={styles.discount}
                  >
                    {currencyformatter(paymentValue?.totalPriceOriginal || 0, property?.currency)}
                  </Typography>
                )}
                {paymentValue?.installmentName && (
                  <Typography variant="body3" color="var(--color-white)">
                    ({paymentValue.installmentName})
                  </Typography>
                )}
              </div>
            )}
          </>
        )}
      {status === 'payment' && paymentValue && (
        <>
          <Typography component="p" variant="body3" color="var(--color-white)" fontWeight={600}>
            {t('checkout_card.price_summary.total_price')}: {property?.currency}{' '}
            {currencyformatter(paymentValue?.totalPrice || 0, property?.currency)}
            {paymentValue?.totalPriceOriginal !== paymentValue?.totalPrice && (
              <Typography
                variant="body3"
                color="var(--color-grey_99)"
                className={styles.discount}
                fontWeight={600}
              >
                {currencyformatter(paymentValue?.totalPriceOriginal || 0, property?.currency)}
              </Typography>
            )}
          </Typography>
          <div className={styles.blackBarItem}>
            <Typography variant="body3" color="var(--color-white)">
              {paymentValue?.installmentName}
            </Typography>
          </div>
        </>
      )}
      {status === 'checkin' && (
        <>
          <Typography
            component="p"
            variant="body3"
            color="var(--color-white)"
            fontWeight={600}
            className={styles.blackBarItemCheckin}
          >
            {bannerText}
          </Typography>
          <div className={styles.blackBarItem}>
            <Typography variant="body3" color="var(--color-white)">
              {t('view_payment')}
            </Typography>
          </div>
        </>
      )}
      {status === 'stay' && (
        <>
          <Typography component="p" variant="body3" color="var(--color-white)" fontWeight={600}>
            {task}
          </Typography>
          <div className={styles.blackBarItem}>
            <Typography variant="body3" color="var(--color-white)">
              {taskCompleted}
            </Typography>
          </div>
        </>
      )}
      {status === 'myPayment' && (
        <>
          <Typography
            component="p"
            variant="body3"
            color="var(--color-white)"
            className={styles.blackBarItemCheckin}
            fontWeight={600}
          >
            {paymentTitle}
          </Typography>
          <div className={classNames(styles.blackBarItem, styles.blackBarItemCheckin)}>
            {paymentLabel}
          </div>
        </>
      )}
      {status === 'amendment' && (
        <>
          <Typography
            component="p"
            variant="body3"
            color="var(--color-white)"
            className={styles.blackBarItemCheckin}
            fontWeight={600}
          >
            {amendmentValue?.title}
          </Typography>
          {!amendmentValue?.isPayToPartner && (
            <div className={classNames(styles.blackBarItem, styles.blackBarItemCheckin)}>
              <Typography variant="body3" color="var(--color-white)">
                {t('checkout_card.price_summary.total_price')}: {amendmentValue?.totalPrice}
              </Typography>
              {!isExtension && Number(amendmentValue?.totalPriceOriginal) > 0 && (
                <Typography
                  variant="body3"
                  color="var(--color-grey_99)"
                  textDecoration="line-through"
                  className={styles.originalPrice}
                >
                  {amendmentValue?.totalPriceOriginal || ''}
                </Typography>
              )}
            </div>
          )}
        </>
      )}
      {status === 'base' && (
        <>
          <Typography
            component="p"
            variant="body3"
            color="var(--color-white)"
            className={styles.blackBarItemCheckin}
            fontWeight={600}
          >
            {baseTitle}
          </Typography>
          {baseValue && (
            <div className={classNames(styles.blackBarItem, styles.blackBarItemCheckin)}>
              <Typography variant="body3" color="var(--color-white)">
                {baseValue}
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlackBar;
